import { useEffect, useRef } from 'react';

export interface ContextMenuItem {
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly separatorBefore?: boolean;
}

export interface ContextMenuState {
  readonly x: number;
  readonly y: number;
  readonly items: readonly ContextMenuItem[];
}

interface ContextMenuProps {
  readonly state: ContextMenuState;
  readonly onClose: () => void;
}

/** 簡易右鍵選單:點外部或按 Esc 關閉。 */
export function ContextMenu({ state, onClose }: ContextMenuProps): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent): void => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="menu"
      data-testid="grid-context-menu"
      className="fixed z-50 min-w-[160px] rounded-md border border-line bg-surface py-1 text-sm shadow-lg"
      style={{ left: state.x, top: state.y }}
    >
      {state.items.map((item) => (
        <div key={item.id}>
          {item.separatorBefore ? (
            <div className="my-1 h-px bg-line" />
          ) : null}
          <button
            type="button"
            role="menuitem"
            disabled={item.disabled}
            className="block w-full px-3 py-1.5 text-left text-fg hover:bg-raised disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => {
              item.onSelect();
              onClose();
            }}
          >
            {item.label}
          </button>
        </div>
      ))}
    </div>
  );
}
